import { Link } from 'react-router-dom';

import { APP_NAME, CREDITS, ROUTES } from '@/lib/constants';
import { Heart } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FooterProps {
  className?: string;
}

/**
 * Editorial footer. Hairline rule above. Two columns on desktop:
 *   (1) credits with "made with ♡ by ..."
 *   (2) legal links
 * Date stamp sits bottom-left as a marginalia.
 */
export function Footer({ className }: FooterProps) {
  const year = new Date().getFullYear();

  return (
    <footer
      className={cn(
        'relative z-10 mt-24 border-t border-ink/15 bg-paper',
        className,
      )}
    >
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid gap-12 md:grid-cols-2">
          {/* Credits */}
          <div>
            <p className="smallcaps text-graphite">Made with</p>
            <div className="mt-3 flex items-center gap-2">
              <Heart
                className="h-4 w-4 text-ochre"
                fill="currentColor"
                strokeWidth={0}
                aria-hidden
              />
              <span className="font-mono text-xs text-graphite">love</span>
            </div>
            <p className="mt-4 font-serif text-lg italic leading-snug text-ink">
              by{' '}
              {CREDITS.map((c, i) => (
                <span key={c.url}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline decoration-ink/30 underline-offset-4 transition-colors hover:decoration-ochre hover:text-ochre"
                  >
                    {c.name}
                  </a>
                  {i < CREDITS.length - 1 ? <span className="text-graphite"> &nbsp;·&nbsp; </span> : null}
                </span>
              ))}
            </p>
          </div>

          {/* Legal */}
          <div>
            <p className="smallcaps text-graphite">Information</p>
            <ul className="mt-3 space-y-1.5">
              <li>
                <Link
                  to={ROUTES.terms}
                  className="text-sm text-ink underline decoration-ink/20 underline-offset-4 transition-colors hover:decoration-ochre hover:text-ochre"
                >
                  Terms &amp; conditions
                </Link>
              </li>
              <li>
                <Link
                  to={ROUTES.privacy}
                  className="text-sm text-ink underline decoration-ink/20 underline-offset-4 transition-colors hover:decoration-ochre hover:text-ochre"
                >
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link
                  to={ROUTES.admin}
                  className="text-sm text-ink underline decoration-ink/20 underline-offset-4 transition-colors hover:decoration-ochre hover:text-ochre"
                >
                  Experimenter panel
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom rule + date stamp */}
        <div className="mt-12 flex flex-col-reverse gap-4 border-t border-ink/10 pt-6 md:flex-row md:items-center md:justify-between">
          <p className="font-mono text-xs text-graphite">
            © {year} {APP_NAME}. All rights reserved.
          </p>
          <p className="font-mono text-xs text-graphite">
            <span className="marginalia">§</span>&nbsp;&nbsp;editorial edition
          </p>
        </div>
      </div>
    </footer>
  );
}
